import { team } from "@/shared/data";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

const roles = [
  {
    key: "owner",
    label: "Owner",
    desc: "Full access · only one per merchant. Cannot be removed by other roles.",
  },
  {
    key: "admin",
    label: "Admin",
    desc: "All operations including settlements, team and account configuration.",
  },
  {
    key: "operator",
    label: "Operator",
    desc: "Pay-outs, customer support, and refunds. Cannot run settlements.",
  },
  {
    key: "developer",
    label: "Developer",
    desc: "API keys, webhooks, sandbox. Read-only on financial data.",
  },
  {
    key: "view",
    label: "View only",
    desc: "Reports, statements, dashboards. No write access anywhere.",
  },
];

export function renderTeam(ctx: Ctx) {
  ctx.canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Settings<span class="page-eyebrow-rule"></span>Team & Permissions</span>
        <h1 class="page-title">Manage who can do what</h1>
      </div>
      <div class="page-head-meta" style="flex-direction: row; align-items: center; gap: 0.6rem;">
        <button class="btn btn-ghost btn-sm">SSO settings</button>
        <button class="btn btn-primary">${icon("plus", 11)} Invite member</button>
      </div>
    </section>

    <section class="grid-2-1">
      <article class="panel">
        <div class="panel-head">
          <div>
            <div class="panel-eyebrow">Members</div>
            <h2 class="panel-title">Active team</h2>
            <p class="panel-sub">${team.length} members · 1 invited · 0 suspended.</p>
          </div>
          <input class="filter-input" type="search" placeholder="Search members" style="max-width: 220px;" />
        </div>
        <div class="ledger-table">
          <div class="ledger-table-header" style="grid-template-columns: 50px 1.4fr 1fr 1.2fr 110px 30px;">
            <span></span>
            <span>Name</span>
            <span>Role</span>
            <span>Scope</span>
            <span>Active</span>
            <span></span>
          </div>
          ${team
            .map(
              (m) => `<div class="ledger-table-row" style="grid-template-columns: 50px 1.4fr 1fr 1.2fr 110px 30px;">
            <div>
              <span class="merchant-seal" style="width: 32px; height: 32px; background: ${
                m.role === "owner"
                  ? "var(--ink-primary)"
                  : m.role === "admin"
                    ? "var(--accent)"
                    : m.role === "operator"
                      ? "var(--state-success)"
                      : m.role === "developer"
                        ? "var(--state-warning)"
                        : "var(--ink-muted)"
              }; color: var(--surface-canvas); font-size: 0.7rem;">${m.initials}</span>
            </div>
            <div class="col-name">
              <strong>${m.name}</strong>
              <span style="font-family: ui-monospace, monospace; font-size: 0.7rem;">${m.email}</span>
            </div>
            <div class="col-method" style="font-weight: 600;">${m.roleLabel}</div>
            <div class="col-method" style="color: var(--ink-muted);">${m.scope}</div>
            <div class="col-status">
              <span class="status-seal is-${m.status === "active" ? "success" : m.status === "invited" ? "pending" : "failed"}"></span>
              ${m.status === "active" ? m.lastActive : m.status === "invited" ? "Invited" : "Suspended"}
            </div>
            <div class="col-chevron">→</div>
          </div>`,
            )
            .join("")}
        </div>
      </article>

      <article class="panel">
        <div class="panel-head">
          <div>
            <div class="panel-eyebrow">Roles</div>
            <h2 class="panel-title">What each role can do</h2>
          </div>
        </div>
        <div class="panel-body" style="padding: 0;">
          ${roles
            .map(
              (r) => `<div style="padding: 1.15rem 1.5rem; border-bottom: 1px solid var(--line-subtle);">
            <div style="display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 0.35rem;">
              <strong style="font-size: 0.92rem; font-weight: 700;">${r.label}</strong>
              <span style="font-size: 0.62rem; letter-spacing: 0.2em; text-transform: uppercase; font-weight: 700; color: var(--ink-muted); font-variant-numeric: tabular-nums;">${team.filter((m) => m.role === r.key).length} member${team.filter((m) => m.role === r.key).length !== 1 ? "s" : ""}</span>
            </div>
            <p style="margin: 0; font-size: 0.78rem; color: var(--ink-muted); line-height: 1.55;">${r.desc}</p>
          </div>`,
            )
            .join("")}
        </div>
      </article>
    </section>

    <section class="panel" style="margin-top: 1.5rem;">
      <div class="panel-head">
        <div>
          <div class="panel-eyebrow">Audit</div>
          <h2 class="panel-title">Recent admin actions</h2>
          <p class="panel-sub">A trail of every privileged action — settlements, role changes, key rotations.</p>
        </div>
        <a class="link-arrow">Full audit log ${icon("arrowRight", 13)}</a>
      </div>
      <div class="ledger-table">
        <div class="ledger-table-header" style="grid-template-columns: 110px 1fr 1.4fr 130px;">
          <span>When</span>
          <span>Actor</span>
          <span>Action</span>
          <span>IP</span>
        </div>
        ${[
          {
            when: "06 May · 09:14",
            actor: "Eleni Mikkelsen",
            action: "Reconciled customer-funds ledger",
            ip: "85.221.…32",
          },
          {
            when: "03 May · 11:08",
            actor: "Dimitrios Halkias",
            action: "Approved settlement STL-2026-018 · €481,200",
            ip: "85.221.…32",
          },
          {
            when: "02 May · 18:47",
            actor: "Joost Verbeek",
            action: "Rotated API key prod_live_••sk7",
            ip: "94.140.…14",
          },
          {
            when: "01 May · 09:00",
            actor: "Eleni Mikkelsen",
            action: "Invited Rania Petrova · View only",
            ip: "85.221.…32",
          },
        ]
          .map(
            (a) => `<div class="ledger-table-row" style="grid-template-columns: 110px 1fr 1.4fr 130px;">
          <div class="col-date" style="font-variant-numeric: tabular-nums; font-weight: 600; color: var(--ink-secondary);">${a.when}</div>
          <div class="col-name"><strong>${a.actor}</strong></div>
          <div class="col-method" style="color: var(--ink-secondary); font-weight: 500;">${a.action}</div>
          <div class="col-status" style="font-family: ui-monospace, monospace; letter-spacing: 0; text-transform: none; font-weight: 500;">${a.ip}</div>
        </div>`,
          )
          .join("")}
      </div>
    </section>
  `;
}
